import { useState, Fragment, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Info, ChevronDown, ChevronRight, Search, X, Filter, RefreshCw, AlertTriangle } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PlanningRawMaterialsPage = () => {
  const [expandedRows, setExpandedRows] = useState({});
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedStockStatus, setSelectedStockStatus] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [hideZeroRows, setHideZeroRows] = useState(false);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);

  // Live data states
  const [items, setItems] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [workOrders, setWorkOrders] = useState([]);

  // Fetch live data on component load
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [itemsResponse, poResponse, woResponse] = await Promise.all([
          axios.get(`${API}/items`),
          axios.get(`${API}/purchase-orders`),
          axios.get(`${API}/work-orders`)
        ]);

        setItems(itemsResponse.data);
        setPurchaseOrders(poResponse.data);
        setWorkOrders(woResponse.data);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load data. Please try refreshing the page.');
        toast.error('Failed to load planning data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Transform live data into material projections
  const transformLiveData = () => {
    if (!items.length) return [];

    // 1. Filter: Only show "Inventory Items" (Raw Materials)
    const rawMaterialsOnly = items.filter(item => 
      item.itemtype === 'InvtPart' || 
      item.item_type_formatted === 'Inventory Item'
    );

    // 2. Pre-calculate Usage from Work Orders
    // Structure: { [itemId]: { [weekIndex]: { qty: 0, details: [] } } }
    const usageMap = {};

    // Calculate Planning Horizon Start (Monday of current week)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dayOfWeek = today.getDay(); // 0=Sun, 1=Mon...
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const horizonStart = new Date(today);
    horizonStart.setDate(today.getDate() - daysToSubtract);

    workOrders.forEach(wo => {
      // Skip if no date or no BOM
      if (!wo.planned_date || !wo.bom_usage || wo.bom_usage.length === 0) return;
      
      // Skip cancelled or finalized orders if you only want "Planned" usage
      // (Usually we include everything scheduled/in-progress/completed for the future, 
      // but exclude past completed orders if we are strictly looking forward. 
      // For simplicity here, we use all dates and filter by week index below.)

      const woDate = new Date(wo.planned_date);
      woDate.setHours(0, 0, 0, 0);
      
      // Calculate which week (0-12) this WO falls into
      const diffTime = woDate.getTime() - horizonStart.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      const weekIndex = Math.floor(diffDays / 7);

      // Only process if it falls within our 13-week view
      if (weekIndex >= 0 && weekIndex < 13) {
        wo.bom_usage.forEach(component => {
          // Use component_id to match Item ID
          const itemId = component.component_id;
          
          if (!usageMap[itemId]) usageMap[itemId] = {};
          if (!usageMap[itemId][weekIndex]) usageMap[itemId][weekIndex] = { qty: 0, details: [] };

          usageMap[itemId][weekIndex].qty += component.planned_qty;
          usageMap[itemId][weekIndex].details.push({
            ref: wo.wo_number,
            qty: component.planned_qty,
            item: `Used in ${wo.item_name}`,
            date: wo.planned_date
          });
        });
      }
    });

    // 3. Build the rows
    return rawMaterialsOnly.map((item, index) => {
      const sku = item.custom_sku || item.itemid || item.displayname; 
      const name = item.displayname || item.itemid || sku;
      const category = item.product_group || item.costcategoryname || 'Uncategorized';
      const unit = item.stock_unit_name || item.unitofmeasure || 'units';
      const itemId = item.id; // Matches component_id

      const itemPurchaseOrders = purchaseOrders.flatMap(po =>
        // MATCHING FIX: Compare exact Internal IDs (item.id vs po.item_id)
        // ROBUST: Allow SKU match even if ID exists but mismatches
        po.items.filter(poItem => 
          (poItem.item_id && String(poItem.item_id) === String(itemId)) || 
          (poItem.sku === sku)
        ).map(poItem => ({
          ...poItem,
          vendor_name: po.vendor_name,
          po_number: po.po_number,
          // OVERRIDE: Use PO Header "Receive By" (mapped to ship_date) as the planning date
          expected_date: po.ship_date || poItem.expected_date
        }))
      );

      // Check for Warnings (Missing Date or Past Date)
      let hasPOWarning = false;
      let poWarningDetails = "";
      
      const todayDate = new Date();
      todayDate.setHours(0,0,0,0);

      itemPurchaseOrders.forEach(po => {
        if (!po.expected_date) {
          hasPOWarning = true;
          poWarningDetails += `${po.po_number}: No Receive By Date; `;
        } else {
          const poDate = new Date(po.expected_date);
          // Fix timezone offset for comparison
          poDate.setMinutes(poDate.getMinutes() + poDate.getTimezoneOffset());
          
          if (poDate < todayDate && (po.quantity_remaining || 0) > 0) {
            hasPOWarning = true;
            poWarningDetails += `${po.po_number}: Past Due (${po.expected_date}); `;
          }
        }
      });

      // Calculate Avg Weekly Usage for "Weeks On Hand" metric only
      // (We sum up the total planned usage for 13 weeks and divide by 13)
      const totalPlannedUsage = usageMap[itemId] 
        ? Object.values(usageMap[itemId]).reduce((acc, w) => acc + w.qty, 0) 
        : 0;
      // If no planned usage, fallback to a tiny number to avoid division by zero issues
      const avgWeeklyUsage = totalPlannedUsage > 0 ? (totalPlannedUsage / 13) : 0;

      // Generate 13 weeks of projections
      const weeks = [];
      let initialInventory = item.total_quantity_on_hand || 0;
      let currentInventory = initialInventory;

      for (let weekIdx = 0; weekIdx < 13; weekIdx++) {
        // Get purchase orders for this week
        const weekStart = new Date(horizonStart);
        weekStart.setDate(horizonStart.getDate() + (weekIdx * 7));
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekStart.getDate() + 6);

        const weekPOs = itemPurchaseOrders.filter(po => {
          if (!po.expected_date) return false;
          
          // FIX: Parse date explicitly as local time to prevent UTC timezone shift
          // Appending 'T00:00:00' forces JS to treat the string as local midnight
          const poDate = new Date(po.expected_date + 'T00:00:00');
          
          // Fix: For the first week (Current Week), include all past due orders
          if (weekIdx === 0) {
            return poDate <= weekEnd;
          }

          // Standard logic for future weeks
          return poDate >= weekStart && poDate <= weekEnd;
        });

        const poTotal = weekPOs.reduce((sum, po) => sum + (po.quantity_remaining || 0), 0);
        const poDetails = weekPOs.map(po => ({
          ref: po.po_number,
          qty: po.quantity_remaining || 0,
          vendor: po.vendor_name,
          date: po.expected_date
        }));

        // Get Planned Usage from our Map
        const usageData = usageMap[itemId]?.[weekIdx];
        const usage = usageData ? usageData.qty : 0;
        const usageDetails = usageData ? usageData.details : [];

        // Calculate ending inventory
        const ending = currentInventory + poTotal - usage;
        const hasIntraWeekNegative = ending < 0;
        // FIX: Allow negative values to show actual shortage
        const displayEnding = ending; 

        weeks.push({
          po: poTotal,
          poDetails,
          usage,
          usageDetails,
          ending: displayEnding,
          hasIntraWeekNegative
        });

        // Update current inventory for next week
        currentInventory = displayEnding;
      }

      return {
        id: index + 1,
        sku,
        name,
        category,
        supplier: 'Unknown', 
        unit,
        avgWeeklyUsage: avgWeeklyUsage || 1, // Fallback for Weeks On Hand calc if 0 usage
        beginningInventory: initialInventory,
        weeks,
        hasPOWarning,
        poWarningDetails
      };
    });
  };

  // Get transformed data
  const rawMaterialsData = transformLiveData();

  const toggleRow = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const openDetailModal = (material) => {
    setSelectedMaterial(material);
    setShowDetailModal(true);
  };
  // Calculate weeks on hand based on inventory and average weekly usage
  const calculateWeeksOnHand = (inventory, avgWeeklyUsage) => {
    if (avgWeeklyUsage === 0) return 999;
    return Math.max(0, inventory / avgWeeklyUsage);
  };

  // Generate week headers as "Week Of" dates (Monday of each week)
  const getWeekHeaders = () => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    
    // Calculate the Monday of the current week
    // If today is Sunday (0), we go back 6 days to get to Monday
    // If today is Monday (1), we stay at today
    // If today is any other day, we go back (dayOfWeek - 1) days
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    const currentMonday = new Date(today);
    currentMonday.setDate(today.getDate() - daysToSubtract);
    
    // Generate 13 week headers
    const weeks = [];
    for (let i = 0; i < 13; i++) {
      const weekDate = new Date(currentMonday);
      weekDate.setDate(currentMonday.getDate() + (i * 7));
      const month = weekDate.getMonth() + 1;
      const day = weekDate.getDate();
      weeks.push(`${month}/${day}`);
    }
    return weeks;
  };

  const weekHeaders = getWeekHeaders();

  // Format date with day abbreviation (e.g., "Wed 10/22")
  const formatDateWithDay = (dateStr) => {
    const date = new Date(dateStr);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const day = days[date.getDay()];
    const month = date.getMonth() + 1;
    const dayNum = date.getDate();
    return `${day} ${month}/${dayNum}`;
  };



  // Get unique categories and suppliers
  const categories = [...new Set(rawMaterialsData.map(m => m.category))];
  const suppliers = [...new Set(rawMaterialsData.map(m => m.supplier))];
  
  // Stock status options
  const stockStatuses = [
    { id: 'critical', label: 'Critical (< 1 week)', color: 'text-red-700' },
    { id: 'low', label: 'Low (1-3 weeks)', color: 'text-yellow-700' },
    { id: 'good', label: 'Good (3-5 weeks)', color: 'text-green-700' },
    { id: 'overstocked', label: 'Overstocked (> 5 weeks)', color: 'text-blue-700' },
    { id: 'intraweek', label: 'Intra-week Negative', color: 'text-orange-700' }
  ];

  // Helper function to determine stock status
  const getStockStatus = (material) => {
    const hasIntraWeekNegative = material.weeks.some(w => w.hasIntraWeekNegative);
    if (hasIntraWeekNegative) return 'intraweek';
    
    const avgWeeksOnHand = material.weeks.reduce((sum, week) => {
      return sum + calculateWeeksOnHand(week.ending, material.avgWeeklyUsage);
    }, 0) / material.weeks.length;
    
    if (avgWeeksOnHand < 1) return 'critical';
    if (avgWeeksOnHand < 3) return 'low';
    if (avgWeeksOnHand <= 5) return 'good';
    return 'overstocked';
  };

  // Filter materials
  const filteredMaterials = rawMaterialsData.filter(material => {
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSKU = material.sku.toLowerCase().includes(search);
      const matchesName = material.name.toLowerCase().includes(search);
      if (!matchesSKU && !matchesName) return false;
    }
    
    // Category filter
    if (selectedCategories.length > 0) {
      if (!selectedCategories.includes(material.category)) return false;
    }
    
    // Supplier filter
    if (selectedSuppliers.length > 0) {
      if (!selectedSuppliers.includes(material.supplier)) return false;
    }
    
    // Stock status filter
    if (selectedStockStatus.length > 0) {
      const status = getStockStatus(material);
      if (!selectedStockStatus.includes(status)) return false;
    }

    // Zero rows filter
    if (hideZeroRows) {
      const hasActivity = material.beginningInventory !== 0 || material.weeks.some(w => w.po !== 0 || w.usage !== 0);
      if (!hasActivity) return false;
    }
    
    return true;
  });

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm("");
    setSelectedCategories([]);
    setSelectedSuppliers([]);
    setSelectedStockStatus([]);
    setHideZeroRows(false);
  };

  // Toggle category selection
  const toggleCategory = (category) => {
    setSelectedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  // Toggle supplier selection
  const toggleSupplier = (supplier) => {
    setSelectedSuppliers(prev => 
      prev.includes(supplier) 
        ? prev.filter(s => s !== supplier)
        : [...prev, supplier]
    );
  };

  // Toggle stock status selection
  const toggleStockStatus = (status) => {
    setSelectedStockStatus(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const getCellColor = (weeksOnHand, hasIntraWeekNegative = false) => {
    if (hasIntraWeekNegative) return "bg-orange-50 text-orange-900 border-2 border-orange-400"; // Warning for intra-week negative
    if (weeksOnHand < 1) return "bg-red-50 text-red-900";
    if (weeksOnHand < 3) return "bg-yellow-50 text-yellow-900";
    if (weeksOnHand <= 5) return "bg-green-50 text-green-900";
    return "bg-blue-50 text-blue-900"; // Overstocked
  };

  // Handle inventory refresh
  const handleRefreshInventory = async () => {
    setRefreshLoading(true);
    try {
      await axios.post(`${API}/sync/inventory-levels`);
      // The WebSocket will handle the UI update
    } catch (error) {
      console.error("Error refreshing inventory:", error);
      toast.error("Failed to refresh inventory levels");
    } finally {
      setRefreshLoading(false);
    }
  };

  // Handle PO sync
  const handleSyncPurchaseOrders = async () => {
    setSyncLoading(true);
    try {
      const response = await fetch(`${API}/sync/purchase-orders`, { // <-- CORRECTED LINE
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        toast.success("Purchase orders sync initiated successfully");
      } else {
        toast.error("Failed to sync purchase orders");
      }
    } catch (error) {
      console.error("Error syncing purchase orders:", error);
      toast.error("Error syncing purchase orders");
    } finally {
      setSyncLoading(false);
    }
  };

  // Show loading state
  if (loading) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading planning data...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Failed to Load Data</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Planning - Raw Materials</h1>
        <p className="text-sm text-gray-600 flex items-center gap-2">
          <Info className="w-4 h-4" />
          Projected inventory: Beginning + Purchase Orders (NetSuite) - Planned Usage
        </p>
      </div>

      {/* Compact Filter Bar */}
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            {/* Search Bar */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by SKU or Material..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Sync POs Button */}
            <button
              onClick={handleSyncPurchaseOrders}
              disabled={syncLoading}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`w-4 h-4 ${syncLoading ? 'animate-spin' : ''}`} />
              {syncLoading ? 'Syncing...' : 'Sync POs'}
            </button>

            {/* Refresh Inventory Button */}
            <button
              onClick={handleRefreshInventory}
              disabled={refreshLoading}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`w-4 h-4 ${refreshLoading ? 'animate-spin' : ''}`} />
              {refreshLoading ? 'Refreshing...' : 'Refresh Inventory'}
            </button>

            {/* Filters Button */}
            <button
              onClick={() => setShowFilterModal(true)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              <Filter className="w-4 h-4" />
              Filters
              {(selectedCategories.length > 0 || selectedSuppliers.length > 0 || selectedStockStatus.length > 0 || hideZeroRows) && (
                <span className="ml-1 px-2 py-0.5 text-xs bg-blue-500 text-white rounded-full">
                  {selectedCategories.length + selectedSuppliers.length + selectedStockStatus.length + (hideZeroRows ? 1 : 0)}
                </span>
              )}
            </button>

            {/* Clear Filters Button */}
            {(searchTerm || selectedCategories.length > 0 || selectedSuppliers.length > 0 || selectedStockStatus.length > 0 || hideZeroRows) && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                <X className="w-4 h-4" />
                Clear
              </button>
            )}
          </div>

          {/* Active Filter Badges */}
          {(selectedCategories.length > 0 || selectedSuppliers.length > 0 || selectedStockStatus.length > 0 || hideZeroRows) && (
            <div className="flex flex-wrap gap-2 mt-3">
              {hideZeroRows && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-gray-800 text-white text-sm rounded-full">
                  Hidden: All Zeros
                  <button onClick={() => setHideZeroRows(false)} className="hover:text-gray-300">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              )}
              {selectedCategories.map(cat => (
                <span key={cat} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                  {cat}
                  <button onClick={() => toggleCategory(cat)} className="hover:text-blue-900">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {selectedSuppliers.map(sup => (
                <span key={sup} className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full">
                  {sup}
                  <button onClick={() => toggleSupplier(sup)} className="hover:text-purple-900">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {selectedStockStatus.map(status => {
                const statusObj = stockStatuses.find(s => s.id === status);
                return (
                  <span key={status} className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-800 text-sm rounded-full">
                    {statusObj?.label}
                    <button onClick={() => toggleStockStatus(status)} className="hover:text-gray-900">
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          )}

          {/* Filter Summary */}
          <div className="mt-3 text-sm text-gray-600">
            Showing {filteredMaterials.length} of {rawMaterialsData.length} materials
          </div>
        </CardContent>
      </Card>

      {/* Filter Modal */}
      <Dialog open={showFilterModal} onOpenChange={setShowFilterModal}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Filter Materials</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Category</label>
              <div className="flex flex-wrap gap-2">
                {categories.map(category => (
                  <button
                    key={category}
                    onClick={() => toggleCategory(category)}
                    className={`px-4 py-2 text-sm rounded-lg border transition-colors ${
                      selectedCategories.includes(category)
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-blue-500'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {/* Supplier Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Supplier</label>
              <div className="flex flex-wrap gap-2">
                {suppliers.map(supplier => (
                  <button
                    key={supplier}
                    onClick={() => toggleSupplier(supplier)}
                    className={`px-4 py-2 text-sm rounded-lg border transition-colors ${
                      selectedSuppliers.includes(supplier)
                        ? 'bg-purple-500 text-white border-purple-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-purple-500'
                    }`}
                  >
                    {supplier}
                  </button>
                ))}
              </div>
            </div>

            {/* Zero Rows Filter */}
            <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <input
                type="checkbox"
                id="hideZeroRows"
                checked={hideZeroRows}
                onChange={(e) => setHideZeroRows(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="hideZeroRows" className="text-sm font-medium text-gray-700 cursor-pointer select-none">
                Hide rows with all zeros (no inventory or activity)
              </label>
            </div>
            
            {/* Stock Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Stock Status</label>
              <div className="space-y-2">
                {stockStatuses.map(status => (
                  <label key={status.id} className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={selectedStockStatus.includes(status.id)}
                      onChange={() => toggleStockStatus(status.id)}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className={`text-sm font-medium ${status.color}`}>{status.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              onClick={() => {
                clearFilters();
                setShowFilterModal(false);
              }}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
            >
              Clear All
            </button>
            <button
              onClick={() => setShowFilterModal(false)}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-colors"
            >
              Apply Filters
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader>
          <CardTitle>Raw Materials - 13 Week Projection</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-auto h-[calc(100vh-280px)] border-b rounded-b-lg">
            <table className="w-full text-xs relative border-separate border-spacing-0">
              <thead className="bg-gray-100">
                <tr>
                  <th className="sticky top-0 z-50 bg-gray-100 text-left py-2 px-1 font-semibold text-gray-700 shadow-sm" style={{left: '0px', minWidth: '30px', width: '30px'}}></th>
                  {/* Merged SKU and Material into Item */}
                  <th className="sticky top-0 z-50 bg-gray-100 text-left py-2 px-2 font-semibold text-gray-700 border-r shadow-sm" style={{left: '30px', minWidth: '250px', width: '250px'}}>Item</th>
                  {/* UOM Column Removed */}
                  {/* Begin Renamed to On Hand */}
                  <th className="sticky top-0 z-50 bg-gray-100 text-center py-2 px-2 font-semibold text-gray-700 border-r bg-blue-50 shadow-sm" style={{left: '280px', minWidth: '100px', width: '100px'}}>On Hand</th>
                  {weekHeaders.map((weekDate, idx) => (
                    <th key={idx} className="sticky top-0 z-40 bg-gray-100 text-center py-3 px-2 font-semibold text-gray-700 border-r shadow-sm" style={{minWidth: '100px'}}>
                      {weekDate}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredMaterials.map((material, idx) => (
                  <>
                    <tr 
                      key={material.id} 
                      className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} cursor-pointer hover:bg-blue-50`}
                      onClick={() => openDetailModal(material)}
                    >
                      <td className="sticky z-30 bg-inherit py-1 px-1" style={{left: '0px'}}>
                        <button 
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRow(material.id);
                          }}
                          className="hover:bg-gray-200 rounded p-0.5"
                        >
                          {expandedRows[material.id] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                        </button>
                      </td>
                      {/* Merged Item Cell */}
                      <td className="sticky z-30 bg-inherit py-2 px-2 border-r" style={{left: '30px'}}>
                        <div className="flex items-center gap-1">
                          <div className={`font-medium ${material.hasPOWarning ? 'text-orange-700' : 'text-gray-900'}`}>
                            {material.sku || material.name}
                          </div>
                          {material.hasPOWarning && (
                            <div title={material.poWarningDetails} className="cursor-help">
                              <AlertTriangle className="w-4 h-4 text-orange-500" />
                            </div>
                          )}
                        </div>
                      </td>
                      {/* On Hand Cell with UOM included */}
                      <td className="sticky z-30 bg-inherit py-2 px-2 text-center font-semibold text-gray-900 border-r bg-blue-50" style={{left: '280px'}}>
                        {Math.round(material.beginningInventory).toLocaleString()} <span className="text-xs text-gray-500 font-normal">{material.unit}</span>
                      </td>
                      {material.weeks.map((week, weekIdx) => {
                        const weeksOnHand = calculateWeeksOnHand(week.ending, material.avgWeeklyUsage);
                        return (
                          <td key={weekIdx} className="py-1 px-2 border-r text-center">
                            <div className={`font-semibold text-xs px-2 py-1 rounded inline-block ${getCellColor(weeksOnHand, week.hasIntraWeekNegative)}`}>
                              {Math.round(week.ending).toLocaleString()}
                              <div className="text-[10px] text-gray-600 mt-0.5">{weeksOnHand.toFixed(1)}w</div>
                              {week.hasIntraWeekNegative && <div className="text-[9px] text-orange-700 font-bold mt-0.5">⚠</div>}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                    {expandedRows[material.id] && (
                      <tr className={idx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                        <td className="sticky z-30 bg-inherit" style={{left: '0px'}}></td>
                        {/* Adjusted colspan for new layout */}
                        <td className="sticky z-30 bg-inherit py-1 px-2 text-xs text-gray-600 border-r" style={{left: '30px'}} colSpan="2">
                          <div className="space-y-1 pl-2">
                            <div className="text-green-700 font-medium">+ Purchase Orders</div>
                            <div className="text-red-700 font-medium">- Planned Usage</div>
                          </div>
                        </td>
                        {material.weeks.map((week, weekIdx) => (
                          <td key={weekIdx} className="py-2 px-2 border-r">
                            <div className="text-xs space-y-1 relative">
                              {/* ... (Keep existing PO/Usage logic inside here) ... */}
                              <div className="text-green-700 group relative inline-block w-full">
                                <span className="cursor-help">+{Math.round(week.po).toLocaleString()}</span>
                                {week.po > 0 && week.poDetails.length > 0 && (
                                  <div className="hidden group-hover:block absolute z-50 bg-gray-900 text-white text-xs rounded p-2 shadow-lg left-0 top-6 w-56">
                                    {week.poDetails.map((po, i) => (
                                      <div key={i} className="mb-1">
                                        <strong>{po.ref}</strong><br />
                                        {po.qty} {material.unit} from {po.vendor}<br />
                                        Due: <span className="text-yellow-300">{formatDateWithDay(po.date)}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="text-red-700 group relative inline-block w-full">
                                <span className="cursor-help">-{Math.round(week.usage).toLocaleString()}</span>
                                {week.usageDetails.length > 0 && (
                                  <div className="hidden group-hover:block absolute z-50 bg-gray-900 text-white text-xs rounded p-2 shadow-lg left-0 top-6 w-56">
                                    {week.usageDetails.map((u, i) => (
                                      <div key={i} className="mb-1">
                                        <strong>{u.ref}</strong><br />
                                        {u.qty} {material.unit} for {u.item}<br />
                                        {u.date && <span>Scheduled: <span className="text-yellow-300">{formatDateWithDay(u.date)}</span></span>}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                        ))}
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="mt-4 flex items-center gap-4 text-xs text-gray-600">
        <div className="flex items-center gap-2">
          <div className="w-12 h-6 bg-orange-50 border-2 border-orange-400 rounded"></div>
          <span>⚠️ Intra-week Negative</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-12 h-6 bg-blue-50 border border-blue-200 rounded"></div>
          <span>Overstocked (5+ weeks)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-12 h-6 bg-green-50 border border-green-200 rounded"></div>
          <span>Good Stock (3-5 weeks)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-12 h-6 bg-yellow-50 border border-yellow-200 rounded"></div>
          <span>Low Stock (1-3 weeks)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-12 h-6 bg-red-50 border border-red-200 rounded"></div>
          <span>Critical (&lt;1 week)</span>
        </div>
      </div>

      {/* Detail Modal */}
      {selectedMaterial && (
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl">{selectedMaterial.sku} - {selectedMaterial.name}</DialogTitle>
              <p className="text-sm text-gray-500">Detailed 13-Week Projection</p>
            </DialogHeader>
            
            <div className="space-y-6">
              {/* Inventory Chart with Weeks on Hand */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Inventory Position & Weeks on Hand</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={selectedMaterial.weeks.map((w, idx) => ({ 
                      week: `W${idx + 1}`, 
                      inventory: w.ending,
                      weeksOnHand: calculateWeeksOnHand(w.ending, selectedMaterial.avgWeeklyUsage)
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="week" />
                      <YAxis yAxisId="left" label={{ value: `Inventory (${selectedMaterial.unit})`, angle: -90, position: 'insideLeft' }} />
                      <YAxis yAxisId="right" orientation="right" label={{ value: 'Weeks on Hand', angle: 90, position: 'insideRight' }} />
                      <Tooltip />
                      <Line yAxisId="left" type="monotone" dataKey="inventory" stroke="#3b82f6" strokeWidth={2} name="Inventory" />
                      <Line yAxisId="right" type="monotone" dataKey="weeksOnHand" stroke="#10b981" strokeWidth={2} name="Weeks on Hand" />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Detailed Breakdown */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Weekly Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {selectedMaterial.weeks.map((week, idx) => {
                      const weeksOnHand = calculateWeeksOnHand(week.ending, selectedMaterial.avgWeeklyUsage);
                      
                      // Calculate dynamic start of week (Monday of current week + offset)
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const dayOfWeek = today.getDay(); 
                      const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
                      const planningStart = new Date(today);
                      planningStart.setDate(today.getDate() - daysToSubtract);
                      
                      const startOfWeek = new Date(planningStart);
                      startOfWeek.setDate(planningStart.getDate() + (idx * 7));

                      const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                      const dayByDay = [];
                      
                      let runningBalance = idx === 0 ? selectedMaterial.beginningInventory : selectedMaterial.weeks[idx - 1].ending;
                      
                      // Create transactions with dates, handling timezone offsets
                      const transactions = [
                        ...week.usageDetails.map(u => {
                            const d = new Date(u.date || startOfWeek);
                            if (u.date) d.setMinutes(d.getMinutes() + d.getTimezoneOffset());
                            return { ...u, type: 'usage', date: d };
                        }),
                        ...week.poDetails.map(po => {
                            const d = new Date(po.date || startOfWeek);
                            if (po.date) d.setMinutes(d.getMinutes() + d.getTimezoneOffset());
                            return { ...po, type: 'po', date: d };
                        })
                      ].sort((a, b) => a.date - b.date);
                      
                      // Generate day-by-day view
                      for (let d = 0; d < 7; d++) {
                        const currentDate = new Date(startOfWeek);
                        currentDate.setDate(startOfWeek.getDate() + d);
                        const dayTransactions = transactions.filter(t => t.date.toDateString() === currentDate.toDateString());
                        
                        dayByDay.push({
                          day: days[d],
                          date: currentDate,
                          transactions: dayTransactions,
                          startBalance: runningBalance
                        });
                        
                        // Update running balance for this day
                        dayTransactions.forEach(t => {
                          if (t.type === 'po') runningBalance += t.qty;
                          else runningBalance -= t.qty;
                        });
                      }
                      
                      return (
                        <div key={idx} className="border rounded-lg p-4 bg-gray-50">
                          <div className="grid grid-cols-3 gap-4 mb-4">
                            <div>
                              <h4 className="font-semibold text-gray-900">Week {idx + 1}</h4>
                              <p className="text-xs text-gray-500">Day-by-Day Breakdown</p>
                            </div>
                            <div className="text-center">
                              <div className={`px-3 py-1 rounded font-semibold text-sm ${getCellColor(weeksOnHand, week.hasIntraWeekNegative)}`}>
                                {week.ending.toLocaleString()} {selectedMaterial.unit}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-semibold text-gray-700">
                                {weeksOnHand.toFixed(1)} Weeks on Hand
                              </div>
                              <div className="text-xs text-gray-500">
                                Avg: {selectedMaterial.avgWeeklyUsage} {selectedMaterial.unit}/wk
                              </div>
                            </div>
                          </div>
                          
                          {/* Day-by-Day Table */}
                          <div className="overflow-x-auto">
                            <table className="w-full text-xs border">
                              <thead className="bg-gray-200">
                                <tr>
                                  <th className="border px-2 py-1 text-left">Day</th>
                                  <th className="border px-2 py-1 text-left">Transaction</th>
                                  <th className="border px-2 py-1 text-right">Amount</th>
                                  <th className="border px-2 py-1 text-right">Balance</th>
                                </tr>
                              </thead>
                              <tbody>
                                {dayByDay.map((day, dayIdx) => {
                                  let dayBalance = day.startBalance;
                                  const rows = [];
                                  
                                  if (day.transactions.length === 0) {
                                    rows.push(
                                      <tr key={`${dayIdx}-empty`} className="bg-white">
                                        <td className="border px-2 py-1 font-medium">{day.day} {day.date.getMonth()+1}/{day.date.getDate()}</td>
                                        <td className="border px-2 py-1 text-gray-400 italic" colSpan="2">No activity</td>
                                        <td className="border px-2 py-1 text-right font-medium">{dayBalance.toLocaleString()}</td>
                                      </tr>
                                    );
                                  } else {
                                    day.transactions.forEach((trans, transIdx) => {
                                      const isPO = trans.type === 'po';
                                      const amount = isPO ? trans.qty : -trans.qty;
                                      dayBalance += amount;
                                      
                                      rows.push(
                                        <tr key={`${dayIdx}-${transIdx}`} className={dayBalance < 0 ? 'bg-red-100' : 'bg-white'}>
                                          {transIdx === 0 && (
                                            <td className="border px-2 py-1 font-medium" rowSpan={day.transactions.length}>
                                              {day.day} {day.date.getMonth()+1}/{day.date.getDate()}
                                            </td>
                                          )}
                                          <td className={`border px-2 py-1 ${isPO ? 'text-green-700' : 'text-red-700'}`}>
                                            <a href="#" className="text-blue-600 hover:underline font-medium">{trans.ref}</a>
                                            <span className="text-gray-600 ml-1">- {isPO ? trans.vendor : trans.item}</span>
                                          </td>
                                          <td className={`border px-2 py-1 text-right font-medium ${isPO ? 'text-green-700' : 'text-red-700'}`}>
                                            {isPO ? '+' : ''}{amount.toLocaleString()}
                                          </td>
                                          <td className={`border px-2 py-1 text-right font-bold ${dayBalance < 0 ? 'text-red-700' : 'text-gray-900'}`}>
                                            {dayBalance.toLocaleString()}
                                          </td>
                                        </tr>
                                      );
                                    });
                                  }
                                  
                                  return <Fragment key={`day-${dayIdx}`}>{rows}</Fragment>;
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default PlanningRawMaterialsPage;
